"use client";

import { AlertTriangle, X } from "lucide-react";

/** One field whose value is about to change and needs a deliberate confirmation. */
export type PendingFieldChange = {
  fieldName: string;
  label: string;
  /** Currently saved value, already made human-readable. */
  fromLabel: string;
  /** Value about to be saved, already made human-readable. */
  toLabel: string;
  /** Why this change matters — shown in the warning box. */
  message?: string;
  /** How long the change takes to take effect, when it is not immediate everywhere. */
  note?: string;
};

type ConfirmFieldChangeDialogProps = {
  changes: PendingFieldChange[];
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
};

/**
 * Blocking confirmation for settings that must never be changed by accident — a field marks
 * itself with `confirmOnChange` in its field config and the form routes its save through here.
 *
 * Shows the exact old → new transition (so a mis-click is obvious rather than plausible), why
 * it matters, and how long it takes to take effect, because a change that applies on a delay
 * is one an admin would otherwise assume had not worked.
 */
export const ConfirmFieldChangeDialog = ({
  changes,
  onConfirm,
  onCancel,
  isLoading = false,
}: ConfirmFieldChangeDialogProps) => {
  if (changes.length === 0) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            {changes.length === 1 ? "Confirm this change" : "Confirm these changes"}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Go back without saving"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-6 space-y-4">
          {changes.map((change) => (
            <div key={change.fieldName}>
              <p className="text-sm font-semibold text-gray-900 mb-2">{change.label}</p>
              <div className="flex items-center gap-2 flex-wrap text-sm mb-3">
                <span className="px-2 py-1 rounded-md bg-gray-100 text-gray-700 line-through">
                  {change.fromLabel}
                </span>
                <span className="text-gray-400">→</span>
                <span className="px-2 py-1 rounded-md bg-amber-100 text-amber-900 font-semibold">
                  {change.toLabel}
                </span>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-900 flex gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    {change.message}
                    {change.note && (
                      <>
                        {" "}
                        <strong>{change.note}</strong>
                      </>
                    )}
                  </span>
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              "Yes, save this change"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmFieldChangeDialog;
